import { Lightbulb } from "lucide-react";

import { FOCUS_RING } from "./lib/focus";
import { cn } from "./lib/utils";
import { Alert } from "./ui/alert";
import { Icon } from "./ui/icon";
import { PinnableTooltip, type TooltipSide } from "./ui/tooltip";

/**
 * v2 MachineScopeMarker (redesign phase 5, #455), composite. Storybook only
 * until implementation; same exports as v1 `components/MachineScopeMarker.tsx`,
 * so the swap is a move.
 *
 * The single marker for content that came from a SIMILAR machine type rather
 * than the one the ticket is about. The rule that holds the design together
 * stays: **silence means verified, ink marks the exception**. A source that
 * matched (or that the scope engine could not judge) renders nothing, so the
 * marker's absence is itself the statement. Hence an icon only: no pill, no
 * label text (break #117: the matrix said "merge → Chip"; it stays an icon on
 * purpose). On an on-type ticket it adds no weight; on a mixed one the eye
 * lands on the one row that carries it.
 *
 * Built on PinnableTooltip: hover and keyboard focus open the sentence, a
 * click or tap pins it, Escape / blur / a click elsewhere unpin. The tooltip
 * is `wide` (320, capped to the viewport): the sentences run 150–190
 * characters. The button is named by the icon's `role="img"` with the full
 * sentence (a `<button>` can't take `role="img"` itself); the tooltip is not
 * a description, so the sentence is read once.
 *
 * Icon: Lightbulb in `--warning-strong` (it says "check this"), 16 next to
 * 14px titles, 12 next to 12px rows. Strings arrive through `labels`; which
 * sentence applies (a type mismatch beats a fact mismatch) is the app's call.
 *
 * Purely informational: verifying takes no click; distrusting uses the fact
 * and source controls that already exist.
 */

export type MachineScopeMarkerSize = 12 | 16;

export interface MachineScopeMarkerLabels {
  /** The full sentence, e.g. "Stammt von einem ähnlichen Maschinentyp (HX 400), …". Empty → nothing. */
  tooltip?: string | null;
}

export interface MachineScopeMarkerProps {
  labels: MachineScopeMarkerLabels;
  size?: MachineScopeMarkerSize;
  side?: TooltipSide;
  /** Start pinned open (static demos). */
  defaultPinned?: boolean;
  className?: string;
}

export function MachineScopeMarker({ labels, size = 16, side, defaultPinned, className }: MachineScopeMarkerProps) {
  if (!labels.tooltip) return null;
  return (
    <PinnableTooltip
      content={labels.tooltip}
      width="wide"
      side={side}
      defaultPinned={defaultPinned}
      aria-describedby={undefined}
      // `-m-1` cancels `p-1`: the hit area grows by 4 on each side without moving the layout.
      className={cn(
        "-m-1 inline-flex shrink-0 cursor-help rounded-sm p-1 align-middle text-warning-strong",
        FOCUS_RING,
        className,
      )}
    >
      <span role="img" aria-label={labels.tooltip} className="inline-flex">
        <Icon icon={Lightbulb} size={size} />
      </span>
    </PinnableTooltip>
  );
}

/** Whether a source carries the marker: the one predicate, so the card, the fact row
 *  and the all-similar note cannot disagree about what "off-type" means. */
export function isMachineScopeMismatch(
  ref: { scopeExpectedLabel?: string | null; scopeFactLabel?: string | null } | null | undefined,
): boolean {
  return !!ref?.scopeExpectedLabel || !!ref?.scopeFactLabel;
}

/**
 * Shown instead of per-card markers when NOTHING in a group matched the
 * customer's machine: with every card marked, the marker stops marking
 * anything. The group says it once, calmly (a static info Alert), and the
 * cards go quiet.
 */
export function AllSimilarMachineNote({ labels, className }: { labels: { note: string }; className?: string }) {
  return (
    <Alert tone="info" announce={false} className={className}>
      {labels.note}
    </Alert>
  );
}

/**
 * The leading slot a source row reserves for the marker, marked or not, so
 * every marker sits on one vertical line and the titles stay aligned. Its
 * width is the icon's (16 or 12).
 */
export function MachineScopeSlot({ size = 16, ...props }: Partial<MachineScopeMarkerProps> & { size?: MachineScopeMarkerSize }) {
  return (
    <span data-slot="machine-scope" className={cn("flex shrink-0 items-center justify-center", size === 16 ? "w-4" : "w-3")}>
      {props.labels?.tooltip ? <MachineScopeMarker size={size} labels={props.labels} {...props} /> : null}
    </span>
  );
}
